// import node module libraries
import { Fragment, useEffect, useState } from 'react';
import { Alert, Col, Row, Breadcrumb, Card, Form, Button, Spinner, Offcanvas, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

// import custom components
import { FormSelect } from 'components/elements/form-select/FormSelect';

// import custom components
import PasswordStrengthMeter from 'components/elements/passwordstrength/PasswordStrengthMeter';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const GeneralSetting = () => {
	const { t, i18n } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isLoading, setIsLoading] = useState(null); // State for loading table
	const [isLoadingButtonUserEdit, setIsLoadingButtonUserEdit] = useState(null); // State for loading spinner
	const [isLoadingButtonChangePassword, setIsLoadingButtonChangePassword] = useState(null); // State for loading spinner
	const [showAlertAccountActivation, setShowAlertAccountActivation] = useState(null); // State for showing alert
	const [showChangePasswordOffcanvas, setShowChangePasswordOffcanvas] = useState(false);
	const handleShowChangePassword = () => setShowChangePasswordOffcanvas(true);
	const handleCloseChangePassword = () => setShowChangePasswordOffcanvas(false);
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);

	const handlePasswordToggle = () => {
		setShowPassword(!showPassword);
	};

	const languages = [
		{ label: t('Choose Language'), value: '' },
		{ label: 'English', value: 'en' },
		{ label: 'German', value: 'de' }
	];

	function onChangeLanguage(e) {
		i18n.changeLanguage(e.target.value);
	}

	const auth = useAuthUser(); // Assuming this gets the auth user data

	// Form Data User
	const [userData, setUserData] = useState({ data: [] }); // State for response data

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// API-Call for all Data
	const getData = async (page) => {
		setIsLoading(true);
		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		// Tenant Data
		await axios.get(`${baseURL}/users/${auth.user_id}`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setUserData(responseData);
				setIsLoading(false);
			})
			.catch((error) => {
				setIsLoading(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	useEffect(() => {
		getData();
	}, []);

	useEffect(() => {
		if(auth && auth.is_active === 0) {
			setShowAlertAccountActivation(true);
		}
	}, []);

	// Edit User
	const handleEditUser = async (e) => {
		e.preventDefault();

		//setIsButtonDisabled(true);
		setIsLoadingButtonUserEdit(true); // Set loading to true

		const data = {
			user_first_name: e.target.firstname.value,
			user_last_name: e.target.lastname.value,
			user_username: e.target.username.value,
			role_id: userData.role_id,
			user_avatar: null, // not implemented yet
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.patch(`${baseURL}/users/${auth.user_id}`, data, options)
			.then(response => {
				handleShowNotification(setNotification, 'success', 'Erfolgreich', t(response.data.message));
				setIsLoadingButtonUserEdit(false); // Stop loading
				//setUserData();
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte überprüfe deine eingegebenen Daten oder kontaktiere den HelpDesk.');
				setIsLoadingButtonUserEdit(false); // Stop loading
			});
	};

	// Edit User
	const handleChangePassword = async (e) => {
		e.preventDefault();

		//setIsButtonDisabled(true);
		setIsLoadingButtonChangePassword(true); // Set loading to true

		const data = {
			username: auth.username,
			oldpassword: e.target.oldpassword.value,
			newpassword: e.target.newpassword.value
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.patch(`${baseURL}/auth/change-password`, data, options)
			.then(response => {
				handleShowNotification(setNotification, 'success', 'Erfolgreich', t(response.data.message));
				setIsLoadingButtonChangePassword(false); // Stop loading
				setShowChangePasswordOffcanvas(false);
				e.target.reset();
				setPassword('');
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte überprüfe deine eingegebenen Daten oder kontaktiere den HelpDesk.');
				setIsLoadingButtonChangePassword(false); // Stop loading
			});
	};

	const [isButtonActivationDisabled, setIsButtonActivationDisabled] = useState(false);

	const handleSendActivationLink = async () => {
		setIsButtonDisabled(true);
		setIsLoading(true); // Set loading to true

		// API-Call Options
		const options = {
			params: {
			},
			headers: {
				Authorization: authHeader,
				Accept: 'application/json'
			}
		};
		const data = {
			username: auth.username,
			otp_valid_for: "activate_account"
		}

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.post(`${baseURL}/auth/generate-otp`, data, options)
			.then(response => {
				setIsButtonActivationDisabled(false);
				setIsLoading(false); // Stop loading
				handleShowNotification(setNotification, 'info', 'Information', response.data.message + '. Bitte überprüfe dein Postfach und folge den Anweisungen in der E-Mail.');

			})
			.catch((error) => {
				setIsButtonActivationDisabled(false);
				setIsLoading(false); // Stop loading
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Deine Nachricht konnte nicht gesendet werden. Bitte versuche es später erneut oder kontaktiere den Support über hello@haeppy-shopping.com.');
			})
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				<Col>
					<Row>
						{/* notifaction user activation */}
						{showAlertAccountActivation && (
						<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
							<Alert variant="warning" className='bg-light-warning text-dark-warning p-4' onClose={() => setShowAlertAccountActivation(false)} dismissible>
								<Alert.Heading className="mb-2">
									<strong>{t('Warning')}</strong>
								</Alert.Heading>
								{t('Your Account is not activated')}.{' '}{t('Please check your Mailbox for Activation Mail or')}{' '}
								<Button variant="outline-dark-warning rounded ms-2" size="sm" onClick={handleSendActivationLink} disabled={isButtonActivationDisabled}>
									{isLoading ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" />
											{t('Resend Activation Link')}
										</>
									) : (
										t('Resend Activation Link')
									)}
								</Button>
							</Alert>
						</Col>
						)}
						{/* end of notifaction user activation */}
						{/* profile  settings */}
						<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
							<Card className="mb-4">
								<Card.Header className="d-flex align-items-center justify-content-between">
									<h4 className="mb-0">{t('Personal Information')}</h4>
								</Card.Header>
								<Card.Body>
									{/* Form */}
									<Form onSubmit={handleEditUser}>
										<Row>
											<Col lg={6} md={12} className="mb-0">
												{/* First Name */}
												<Form.Group className="mb-3">
													<Form.Label htmlFor="firstname">{t('First Name')}</Form.Label>
													<Form.Control
														type="text"
														id="firstname"
														placeholder={t('Enter First Name')}
														autoComplete="given-name"
														defaultValue={userData ? userData.user_first_name : ''}
														required
													/>
												</Form.Group>
											</Col>
											<Col lg={6} md={12} className="mb-0">
												{/* Last Name */}
												<Form.Group className="mb-3">
													<Form.Label htmlFor="lastname">{t('Last Name')}</Form.Label>
													<Form.Control
														type="text"
														id="lastname"
														placeholder={t('Enter Last Name')}
														autoComplete="family-name"
														defaultValue={userData ? userData.user_last_name : ''}
														required
													/>
												</Form.Group>
											</Col>
											<Col lg={6} md={12} className="mb-0">
												{/* E-Mail */}
												<Form.Group className="mb-3">
													<Form.Label htmlFor="username">{t('Email')}</Form.Label>
													<Form.Control
														type="email"
														id="username"
														placeholder={t('Enter Email')}
														autoComplete="username"
														defaultValue={userData ? userData.user_username : ''}
														required
													/>
													<small>
														{t('This email will be used for login and communication')}
													</small>
												</Form.Group>
											</Col>
											<Col lg={6} md={12} className="mb-0">
												{/* Password */}
												<Form.Group className="mb-3">
													<Form.Label htmlFor="password">{t('Password')}</Form.Label>
													<Button variant="outline-secondary" className="d-block w-100" onClick={() => handleShowChangePassword()}>
														{t('Change Password')}
													</Button>
												</Form.Group>
											</Col>
										</Row>
										<div className="d-flex justify-content-end mt-4">
											<Button className="ms-2 d-end" variant="primary" type="submit" disabled={isLoadingButtonUserEdit}>
												{isLoadingButtonUserEdit ? (
													<>
														<Spinner animation="border" size="sm" className="me-2" />
														{t('Update Profile')}
													</>
												) : (
														t('Update Profile')
												)}
											</Button>
										</div>
									</Form>
								</Card.Body>
							</Card>
						</Col>
						{/* end of profile settings */}
						{/* language settings form */}
						<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
							<Card className="mb-4">
								<Card.Header className="d-flex align-items-center justify-content-between">
									<h4 className="mb-0">{t('Language Settings')}</h4>
									<span>{t('Select a preferred language for the dashboard')}</span>
								</Card.Header>
								<Card.Body>
									<Form>
										<Row>
											<Col lg={12} md={12} className="mb-3">
												<FormSelect
													isMulti={false}
													onChange={onChangeLanguage}
													options={languages}
													defaultValue={languages.find(lang => lang.value === i18n.language)}
												/>
												<small>
													{t('Your browser language was automatically recognized and set to:')} {t(languages.find(lang => lang.value === i18n.language)?.label)}
												</small>
											</Col>
										</Row>
									</Form>
								</Card.Body>
							</Card>
						</Col>
						{/* end of language settings form */}
					</Row>
				</Col>
			</Row>

			{/* Edit User Offcanvas */}
			<Offcanvas show={showChangePasswordOffcanvas} onHide={handleCloseChangePassword} placement="end">
				<Offcanvas.Header closeButton>
					<Offcanvas.Title><h2>{t('Change Password')}</h2></Offcanvas.Title>
				</Offcanvas.Header>
				<Offcanvas.Body>
					{/* Form */}
					<Form onSubmit={handleChangePassword}>
						<Card className="mb-4">
							<Card.Header className="card-header-height d-flex justify-content-between align-items-center">
								<h4 className="mb-0">{t('Change Password')}</h4>
							</Card.Header>
							<Card.Body className="">
								<Row className="mb-4">
									<Col lg={12} md={12} className="mb-0">
										{/* Old Password */}
										<Form.Group className="mb-3">
											<Form.Label htmlFor="oldpassword">{t('Old Password')}</Form.Label>
											<div className="input-group">	
												<Form.Control
													type={showPassword ? 'text' : 'password'}
													id="oldpassword"
													placeholder={t('Enter Old Password')}
													autoComplete="password"
													required
												/>
												<span className="input-group-text" onClick={handlePasswordToggle}>
													<i className={`fe ${showPassword ? 'fe-eye-off' : 'fe-eye'}`}></i>
												</span>
											</div>
										</Form.Group>
									</Col>
									<Col lg={12} md={12} className="mb-0">
										{/* New Password */}
										<Form.Group className="mb-3">
											<Form.Label htmlFor="newpassword">{t('New Password')}</Form.Label>
											<div className="input-group">
												<Form.Control
													type={showPassword ? 'text' : 'password'}
													id="newpassword"
													placeholder={t('Enter New Password')}
													autoComplete="new-password"
													value={password}
													onChange={(e) => setPassword(e.target.value)}
													required
												/>
												<span className="input-group-text" onClick={handlePasswordToggle}>
													<i className={`fe ${showPassword ? 'fe-eye-off' : 'fe-eye'}`}></i>
												</span>
											</div>
										</Form.Group>
										{/* Password strength meter */}
										{password.length > 0 ? (
											<>
												<Row className="align-items-center g-0">
													<Col sm={6}>
														<span
															data-bs-toggle="tooltip"
															data-placement="right"
															title=""
														>
															Passwortstärke
															<OverlayTrigger
																key="top"
																placement="top"
																overlay={
																	<Tooltip id="tooltip-top">
																		Wir helfen dir ein ausreichend sicheres Passwort zu setzen.
																		Für die beste Sicherheit sollte dein Passwort aus mindestens 6 Zeichen, einem Großbuchstaben und einer Ziffer bestehen.
																	</Tooltip>
																}
															>
																<i className="fas fa-question-circle ms-1"></i>
															</OverlayTrigger>
														</span>
													</Col>
												</Row>
												<PasswordStrengthMeter password={password} />
											</>
										) : null}
									</Col>
								</Row>
							</Card.Body>
						</Card>
						<div className="d-flex justify-content-end mt-4">
							<Button variant="secondary" onClick={handleCloseChangePassword}>
								{t('Cancel')}
							</Button>
							<Button className="ms-2 d-end" variant="primary" type="submit" disabled={isLoadingButtonChangePassword}>
								{isLoadingButtonChangePassword ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" />
										{t('Change Password')}
									</>
								) : (
										t('Change Password')
								)}
							</Button>
						</div>
					</Form>
				</Offcanvas.Body>
			</Offcanvas>
		</Fragment>
	);
};

export default GeneralSetting;
